import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCourse, loc } from "@/lib/physicsApi";
import { fetchTrackRecords } from "@/lib/academyProgress";
import { PhysicsImage, EmeraldCosmos, STATUS_LABELS } from "./PhysicsImage";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { ResumeButton, StatusBadge, PhaseProgress } from "@/components/academy/LessonNav";
import { tpl } from "@/lib/academyTheme";

export function phaseStatus(phase, records) {
  const slugs = phase.lessons.map((l) => l.slug);
  const recs = records.filter((r) => slugs.includes(r.lessonSlug));
  if (!recs.length) return "none";
  if (recs.some((r) => r.learningStatus === "Requires Further Practice")) return "renew";
  const strong = recs.filter((r) => ["Practised", "Integrated", "Independent", "Demonstrated"].includes(r.learningStatus));
  if (recs.length === slugs.length && strong.length === slugs.length) return "done";
  if (recs.length === slugs.length) return "ready";
  return "active";
}

export default function PhysicsCourse() {
  const { lang, t } = useT();
  const [course, setCourse] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    fetchCourse().then(setCourse).catch(() => {});
    fetchTrackRecords("physics").then(setRecords);
  }, []);

  const lessonList = course
    ? course.phases.flatMap((p) => p.lessons.map((l) => ({ p, l })))
    : [];

  useSEO({
    title: "Stoic Physics Course | Nature, Fate and Inner Freedom | StoicOs.ai",
    description: "Explore Stoic physics as a practical discipline of reality, causality, fate, change, agency and living according to nature.",
    canonical: langCanonical("/academy/physics", lang),
    alternates: langAlternates("/academy/physics"),
    og: { title: "Stoic Physics: The Hall of Nature & Fate", description: "Reality, causality, fate, change, agency and living according to nature.", type: "article" },
    jsonLd: course ? [
      { "@context": "https://schema.org", "@type": "Course",
        name: "Stoic Physics: The Hall of Nature & Fate",
        description: "A practical Stoic discipline of reality, causality, fate, change, agency and living according to nature.",
        provider: { "@type": "Organization", name: "StoicOs.ai", url: "https://stoicos.ai" },
        url: "https://stoicos.ai/academy/physics" },
      { "@context": "https://schema.org", "@type": "ItemList",
        itemListElement: lessonList.map(({ p, l }, i) => ({
          "@type": "ListItem", position: i + 1, name: loc(l.title),
          url: `https://stoicos.ai/academy/physics/phase/${p.slug}/lesson/${l.slug}` })) },
    ] : [],
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  const media = course.media || {};
  const returning = records.length > 0;
  const last = returning ? [...records].sort((a, b) => (b.lastActivityAt || "").localeCompare(a.lastActivityAt || ""))[0] : null;
  const allLessons = course.phases.flatMap((p) => p.lessons.map((l) => ({ ...l, phaseSlug: p.slug })));
  const lastLesson = last ? allLessons.find((l) => l.slug === last.lessonSlug) : null;
  const firstLesson = allLessons[0];
  const continueTo = lastLesson ? `/academy/physics/phase/${lastLesson.phaseSlug}/lesson/${lastLesson.slug}` : `/academy/physics/phase/${firstLesson.phaseSlug}/lesson/${firstLesson.slug}`;

  return (
    <main className="min-h-screen bg-ink" data-testid="physics-course-page">
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <PhysicsImage media={media} assetKey="physics-00-hall-of-nature-and-fate" ratio="" className="absolute inset-0" alt="The Hall of Nature & Fate" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 pb-16 pt-32 w-full">
          <DisciplineSwitcher active="physics" />
          <div className="flex items-center gap-4 mb-6"><EmeraldCosmos size={44} /><span className="text-[10px] tracking-[0.35em] uppercase text-[#7fb69a]">Academy of Practical Wisdom · Discipline II</span></div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone tracking-tight">STOIC PHYSICS — THE HALL OF NATURE & FATE</h1>
          <p className="mt-6 max-w-2xl text-fog font-reading leading-[1.9]">{loc(course.introduction, lang)}</p>
          <p className="mt-3 max-w-2xl text-fog/80 font-reading">{loc(course.heroIntro, lang)}</p>
          <div className="mt-10 flex flex-wrap gap-4" data-testid="physics-enter-hall">
            <ResumeButton discipline="physics" course={course} records={records} />
          </div>
        </div>
      </section>

      {/* Course roadmap — the six phases as one continuous journey */}
      <section className="border-t border-white/5 py-14 px-6 sm:px-10" data-testid="physics-roadmap-section">
        <div className="max-w-5xl mx-auto">
          <PhysicsImage media={media} assetKey="physics-15-course-roadmap" ratio="aspect-[16/9]"
            alt="Roadmap van de zes fasen van de leergang Stoïsche Fysica." />
        </div>
      </section>

      <section className="border-t border-white/5 py-14 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto border-l-2 border-[#7fb69a]/60 pl-6 py-1" data-testid="physics-historical-note">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a] mb-2">Historical Note</p>
          <p className="text-sm text-fog/85 font-reading leading-[1.85]">{loc(course.historicalNote, lang)}</p>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <p className="font-serif text-2xl md:text-3xl text-bone">{course.fullMovement.join("  →  ")}</p>
          <p className="text-xs tracking-[0.22em] uppercase text-[#7fb69a]">{loc(course.thresholdLine, lang)}</p>
        </div>
      </section>

      <section className="border-t border-white/5 py-20 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto border border-[#7fb69a]/45 shadow-[0_0_28px_rgba(127,182,154,0.22)] rounded-sm p-6 md:p-10" data-testid="physics-course-frame">
          <h2 className="font-serif text-2xl md:text-3xl text-bone mb-12 text-center">{lang === "nl" ? "De Zes Fasen" : "The Six Phases"}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {course.phases.map((p) => {
              const st = phaseStatus(p, records);
              return (
                <div key={p.slug} data-testid={`physics-phase-card-${p.slug}-wrap`}
                  className="group border border-[#c8a95a]/20 bg-[#14211a] hover:border-gold/50 transition-colors p-8 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl text-bone">{["I","II","III","IV","V","VI"][p.order - 1]} · {loc(p.title, lang)}</h3>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${st === "done" ? "border-gold/60 text-gold" : st === "none" ? "border-emerald-100/25 text-emerald-100/40" : "border-[#7fb69a]/50 text-[#bfdccb]"}`}>{STATUS_LABELS[st]}</span>
                    <PhaseProgress phase={p} records={records} accent="#7fb69a" />
                  </div>
                  <Link to={`/academy/physics/phase/${p.slug}`} data-testid={`physics-phase-card-${p.slug}`}
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase bg-[#2e5040] text-[#eaf5ee] hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7fb69a]">
                    {t("academy.nav.openPhase")} <span aria-hidden>→</span>
                  </Link>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-gold/60">{loc(p.movement, lang)}</p>
                  <p className="text-sm text-fog/80 font-reading leading-relaxed">{loc(p.centralQuestion, lang)}</p>
                  <p className="text-xs text-[#7fb69a] mt-auto pt-2 border-t border-white/5">{loc(p.coreAbility, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto border border-[#c8a95a]/20 bg-[#14211a] p-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a] mb-4">The Instruments of the Discipline</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-xs tracking-[0.18em] uppercase">
            <Link to="/academy/physics/record" className="text-gold hover:underline" data-testid="physics-record-link">Record of Reality</Link>
            <Link to="/academy/physics/practice" className="text-[#bfdccb] hover:underline" data-testid="physics-practice-link">Practice of Acceptance</Link>
            <Link to="/academy/physics/path" className="text-[#bfdccb] hover:underline" data-testid="physics-path-link">Path of Nature</Link>
            <Link to="/academy/physics/terms" className="text-[#bfdccb] hover:underline" data-testid="physics-terms-link">The Terms</Link>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 py-16 px-6 sm:px-10">
        <div className="max-w-3xl mx-auto flex items-center gap-6">
          <div className="w-28 shrink-0 opacity-60"><PhysicsImage media={media} assetKey="physics-14-chamber-of-nature-and-fate" ratio="aspect-[3/2]" alt="" /></div>
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#7fb69a]">Final Assessment</p>
            <Link to="/academy/physics/chamber" className="font-serif text-lg text-bone mt-1 hover:text-gold block" data-testid="physics-chamber-link">The Chamber of Nature & Fate</Link>
            <p className="text-sm text-fog/70 mt-1">Opens when the six phases have been walked.</p>
          </div>
        </div>
      </section>
    </main>
  );
}
